import React, { useCallback, useEffect, useState } from "react";
import SitelineHome from "./SitelineHome";
import "../styles/siteline.css";

/**
 * Desktop-only floating siteline dock.
 *
 * The home page used to carry the siteline as a right rail with its own
 * internal scroll, which gave the page two competing wheel targets. The rail is
 * gone; the feed now lives behind this chip and opens as a right-side drawer,
 * so the home itself is one plain document scroll.
 *
 * Placement (see also agent_chat_widget.css — same rail):
 *   sitebot chip   right 16px · bottom 44px  · z 890
 *   siteline chip  right 16px · bottom 96px  · z 880
 *   siteline panel right 16px · bottom 146px · z 881
 * Toasts sit bottom-LEFT, the status bar is 26px tall, and the two drawers are
 * mutually exclusive (opening the chat closes this one and hides the chip,
 * since the chat panel grows up through the same column).
 */

const DOCK_KEY = "siteline:dockOpen";

function readStored(): boolean {
  try {
    return localStorage.getItem(DOCK_KEY) === "1";
  } catch {
    return false;
  }
}

const SitelineDock: React.FC = () => {
  const [open, setOpen] = useState<boolean>(readStored);
  // keep the feed unmounted until the drawer is opened once — collapsed is the
  // default, and an unopened dock should not fetch the feed
  const [mounted, setMounted] = useState<boolean>(open);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (open) setMounted(true);
    try {
      localStorage.setItem(DOCK_KEY, open ? "1" : "0");
    } catch {
      // private mode — the preference just doesn't stick
    }
    window.dispatchEvent(
      new CustomEvent("n450s:siteline-dock-change", { detail: { open } }),
    );
  }, [open]);

  // the sitebot panel opens from the same bottom-right column — one drawer at a time
  useEffect(() => {
    const handler = (e: Event) => {
      const isOpen = !!(e as CustomEvent).detail?.open;
      setChatOpen(isOpen);
      if (isOpen) setOpen(false);
    };
    window.addEventListener("n450s:chat-panel-change", handler);
    return () => window.removeEventListener("n450s:chat-panel-change", handler);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  return (
    <>
      <button
        type="button"
        className={
          "siteline-dock-chip" +
          (open ? " is-open" : "") +
          (chatOpen ? " is-stashed" : "")
        }
        onClick={toggle}
        aria-expanded={open}
        aria-controls="siteline-dock-panel"
        aria-label={open ? "close the siteline" : "open the siteline"}
      >
        <span className="siteline-dock-chip__label">siteline</span>
        <span className="live-dot" aria-hidden />
        <span className="siteline-dock-chip__caret" aria-hidden>
          {open ? "▾" : "▴"}
        </span>
      </button>

      <div
        id="siteline-dock-panel"
        className={"siteline-dock-panel" + (open ? " is-open" : "")}
        role="region"
        aria-label="siteline"
      >
        {mounted && <SitelineHome showWhenEmpty className="siteline-home--dock" />}
      </div>
    </>
  );
};

export default SitelineDock;
